import { useState } from "react";
import type { Asset } from "@forgepoint/shared";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { FileViewer } from "./FileViewer";
import { FileViewerExpandButton } from "./FileViewerExpandButton";

interface AssetViewerProps {
  asset: Asset;
  className?: string;
}

/**
 * Component that normalizes Asset data and delegates rendering to FileViewer.
 * Handles web-optimized views when available, falling back to original asset URLs.
 * Its full screen control opens a second viewer in a full-viewport dialog.
 *
 * @param asset - The Asset object containing file information and URLs
 * @param className - Optional CSS classes to apply to the viewer
 * @returns The inline FileViewer and its full screen dialog
 */
export function AssetViewer({ asset, className }: AssetViewerProps) {
  const [expanded, setExpanded] = useState(false);
  const viewUrl = asset.webViewExternalUrl || asset.externalUrl;
  const fileType = asset.webViewFileType || asset.fileType || "";
  const fileName = (asset.webViewFileName ?? asset.fileName).toLowerCase();
  const displayName = asset.fileName || "";
  const file = { viewUrl, fileType, fileName, displayName };

  return (
    <>
      <FileViewer
        {...file}
        className={className}
        viewControl={
          <FileViewerExpandButton
            expanded={false}
            onClick={() => setExpanded(true)}
          />
        }
      />
      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent
          aria-describedby={undefined}
          className="inset-0 flex h-full w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 p-0 sm:rounded-none [&>button]:hidden"
        >
          <DialogTitle className="sr-only">{displayName}</DialogTitle>
          <FileViewer
            {...file}
            viewControl={
              <FileViewerExpandButton
                expanded={true}
                onClick={() => setExpanded(false)}
              />
            }
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
